import styled from "styled-components";
import { CSSVars } from "@/styles/styles.config";

export const StyledPlayground = styled.section`
  background:
    radial-gradient(circle at 15% 20%, rgba(91, 91, 255, 0.14) 0%, transparent 55%),
    radial-gradient(circle at 85% 15%, rgba(245, 138, 138, 0.16) 0%, transparent 55%),
    linear-gradient(180deg, #ffffff 0%, #f4f6fc 100%);
  padding: 36px 20px;
  position: relative;
  overflow: hidden;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 44px 30px;
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 52px 40px;
  }

  /* Widened: this section is the interactive centrepiece, so it runs wider
     than the page's normal text column. */
  /* Elfsight-style page dim: a fixed viewport scrim that darkens the rest of
     the page as the section scrolls in (opacity is driven from JS). Sits above
     the page but below the playground content, so the editor + heading stay
     bright and pop. pointer-events:none so it never blocks interaction. */
  .pg-dim {
    position: fixed;
    inset: 0;
    background: #0a0a12;
    pointer-events: none;
    z-index: 40;
    transition: opacity 0.12s linear;
    will-change: opacity;
  }

  .pg-inner {
    max-width: 1600px;
    margin: 0 auto;
    position: relative;
    /* Above the dim scrim so the editor + heading stand out. */
    z-index: 41;
  }

  /* When the page dims, the heading + microcopy float above the dark scrim,
     so lighten their text for contrast. Only applies to dim-enabled instances;
     the normal light-background layout is untouched. */
  &.is-dimmed {
    .pg-eyebrow {
      color: #b9c0ff;
    }
    .pg-title {
      color: ${CSSVars.white};
    }
    .pg-sub {
      color: rgba(255, 255, 255, 0.78);
    }
    .pg-microcopy {
      color: rgba(255, 255, 255, 0.82);
    }
  }

  .pg-head {
    text-align: center;
    max-width: 760px;
    margin: 0 auto 18px;
  }

  .pg-eyebrow {
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${CSSVars.blue500};
    margin: 0 0 7px;
    transition: color 0.2s ease;
  }

  .pg-title {
    font-family: ${CSSVars.primaryFont};
    font-weight: 700;
    font-size: 22px;
    line-height: 1.2;
    letter-spacing: -0.015em;
    color: ${CSSVars.black};
    margin: 0 0 9px;
    transition: color 0.2s ease;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: 26px;
    }
    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      font-size: 30px;
    }
  }

  .pg-sub {
    font-family: ${CSSVars.secondaryFont};
    font-weight: 300;
    font-size: 15px;
    line-height: 1.45;
    color: #4b5563;
    margin: 0;
    transition: color 0.2s ease;
  }

  /* ----- template switcher ----- */
  .pg-templates {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin: 0 0 22px;
  }

  .pg-template {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 14px;
    color: ${CSSVars.black};
    background: #ffffff;
    border: 1px solid #e2e5ee;
    border-radius: 999px;
    padding: 8px 18px 8px 9px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease,
      transform 0.15s ease, color 0.2s ease;

    .swatch {
      width: 22px;
      height: 22px;
      border-radius: 6px;
      background-size: cover;
      background-position: center;
      flex: none;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
    }

    &:hover {
      border-color: ${CSSVars.blue500};
      transform: translateY(-1px);
    }

    &.is-active {
      color: ${CSSVars.white};
      border-color: transparent;
      background: linear-gradient(
        270deg,
        ${CSSVars.pink300} 0%,
        ${CSSVars.purple500} 50%,
        ${CSSVars.blue500} 100%
      );
      box-shadow: 0 6px 18px rgba(91, 91, 255, 0.28);

      .swatch {
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
      }
    }
  }

  /* ----- framed stage (facade OR live editor) ----- */
  .pg-frame {
    background: #ffffff;
    border: 1px solid #e6e8f0;
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(17, 24, 39, 0.12);
    overflow: hidden;
    max-width: 1520px;
    margin: 0 auto;
  }

  .pg-bar {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 13px 16px;
    border-bottom: 1px solid #eef0f6;
    background: #fbfbfe;

    /* Direct children only: the three window dots. */
    > span {
      width: 11px;
      height: 11px;
      border-radius: 50%;
      background: #dfe2ec;
    }
    > span:nth-child(1) {
      background: #ff9d9d;
    }
    > span:nth-child(2) {
      background: #ffd479;
    }
    > span:nth-child(3) {
      background: #8fd39a;
    }
  }

  /* Fixed height reserves layout space up front so nothing shifts when the
     iframe mounts (protects CLS). */
  .pg-stage {
    position: relative;
    width: 100%;
    height: 520px;
    background: #ffffff;
    overflow: hidden;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      height: 720px;
    }

    iframe {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  /* Trim: pull the editor's top toolbar out of the visible area. Works even
     across origins (pure CSS, no DOM access needed). --crop set inline. */
  .pg-stage.is-cropped iframe {
    height: calc(100% + var(--crop, 0px));
    margin-top: calc(var(--crop, 0px) * -1);
  }

  /* The live editor sits under the facade and fades in once loaded. */
  .pg-editor {
    z-index: 1;
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  .pg-editor.is-shown {
    opacity: 1;
  }

  /* ----- facade (fast; no heavy editor until the visitor opts in) -----
     A blurred preview of the selected look sits behind a "Try it now"
     unblocker. Clicking anywhere swaps in the real editor. */
  .pg-facade {
    position: absolute;
    inset: 0;
    z-index: 2;
    border: 0;
    padding: 0;
    cursor: pointer;
    background: #0b1020;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    opacity: 1;
    transition: opacity 0.5s ease;
  }
  .pg-facade.is-gone {
    opacity: 0;
    pointer-events: none;
  }

  /* Blurred preview layer. Scaled up slightly so the blur has no soft edges. */
  .pg-blur {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(9px) brightness(0.92) saturate(1.05);
    transform: scale(1.1);
    transition: filter 0.3s ease;
  }
  .pg-facade::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(9, 12, 26, 0.28) 0%,
      rgba(9, 12, 26, 0.42) 100%
    );
  }
  .pg-facade:hover .pg-blur {
    filter: blur(7px) brightness(0.96) saturate(1.05);
  }

  .pg-unlock {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    pointer-events: none;
  }

  .pg-try {
    font-family: ${CSSVars.primaryFont};
    font-weight: 700;
    font-size: 18px;
    color: ${CSSVars.white};
    padding: 16px 40px;
    border-radius: 999px;
    background: linear-gradient(
      270deg,
      ${CSSVars.pink300} 0%,
      ${CSSVars.purple500} 50%,
      ${CSSVars.blue500} 100%
    );
    background-size: 200% 100%;
    background-position: 100% 0;
    box-shadow: 0 12px 34px rgba(91, 91, 255, 0.5);
    transition: transform 0.2s ease, background-position 0.4s ease,
      box-shadow 0.2s ease;
  }
  .pg-facade:hover .pg-try {
    transform: translateY(-2px);
    background-position: 0 0;
    box-shadow: 0 16px 40px rgba(245, 138, 138, 0.5);
  }

  .pg-try-sub {
    font-family: ${CSSVars.secondaryFont};
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
  }

  /* Spinner shown while the live editor boots after launch. */
  .pg-loading {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    color: #9aa1b2;
    font-family: ${CSSVars.secondaryFont};
    font-size: 14px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f7fb 100%);
    pointer-events: none;
  }
  .pg-loading .dot {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 3px solid #e2e5ee;
    border-top-color: ${CSSVars.blue500};
    animation: pg-spin 0.9s linear infinite;
  }
  @keyframes pg-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ----- CTA ----- */
  /* Desktop: just the reassurance line under the editor. Picking a template
     inside the editor is the call to action now. */
  .pg-cta-row {
    text-align: center;
    margin: 12px 0 0;
  }

  .pg-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${CSSVars.primaryFont};
    font-weight: 700;
    font-size: 14px;
    color: ${CSSVars.white};
    text-decoration: none;
    padding: 10px 24px;
    border-radius: 999px;
    background: linear-gradient(
      270deg,
      ${CSSVars.pink300} 0%,
      ${CSSVars.purple500} 50%,
      ${CSSVars.blue500} 100%
    );
    background-size: 200% 100%;
    background-position: 100% 0;
    transition: background-position 0.4s ease, transform 0.2s ease,
      box-shadow 0.2s ease;
    box-shadow: 0 6px 18px rgba(91, 91, 255, 0.3);

    &:hover {
      background-position: 0 0;
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(245, 138, 138, 0.4);
    }

    span {
      transition: transform 0.2s ease;
    }
    &:hover span {
      transform: translateX(3px);
    }

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: 15px;
      padding: 12px 28px;
    }
  }

  .pg-microcopy {
    font-family: ${CSSVars.secondaryFont};
    font-size: 13px;
    color: #6b7280;
    margin: 9px 0 0;
    transition: color 0.2s ease;
  }

  /* Responsive: full editor embed on tablet+/desktop; a single CTA on phones.
     Below breakpointMD the embed (and its poster) is not rendered at all, so it
     costs nothing on mobile and the cramped in-phone editor is avoided. */
  .pg-mobile {
    text-align: center;
    margin-top: 4px;
  }
  .pg-frame,
  .pg-cta-row {
    display: none;
  }
  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    .pg-mobile {
      display: none;
    }
    .pg-frame {
      display: block;
    }
    .pg-cta-row {
      display: block;
    }
  }
`;
